import { resolveCatalogObjectUrl } from "@/music-catalog/utils/catalog-view";

import type { LibraryProject } from "@/cloud/types/types";
import type { TranslationKey } from "@/localization/hooks/use-translation";

/** Which picture of a project this is. */
export type ProjectArtworkPanelId = "front" | "back" | "disc";

/** Which arrangement of the project's pictures the detail page is showing. */
export type ProjectArtworkViewId = "preview" | "disc" | "front" | "back";

export interface ProjectArtworkPanel {
  id: ProjectArtworkPanelId;
  /** What this picture is called, resolved by the renderer rather than here. */
  labelKey: TranslationKey;
  url: string;
}

export type ProjectArtworkView =
  | {
      id: "preview";
      /** What this arrangement is called, resolved by the renderer rather than here. */
      labelKey: TranslationKey;
      panels: readonly [ProjectArtworkPanel, ProjectArtworkPanel];
    }
  | {
      id: Exclude<ProjectArtworkViewId, "preview">;
      /** What this picture is called, resolved by the renderer rather than here. */
      labelKey: TranslationKey;
      panels: readonly [ProjectArtworkPanel];
    };

/**
 * The pictures of a project worth looking at, in the order somebody handling the
 * case would meet them.
 *
 * Front first, because that is what an album *is* to the person looking for it.
 * The disc used to lead this page for the only reason that it was the only
 * picture the Cloud Library carried — which meant every album here was
 * identified by its label, the one part of it nobody sees on a shelf.
 *
 * **The printed spine is deliberately not here.** A head carries one, and this
 * page could draw it, but a spine is 6.5mm of artwork on a 118mm page: shown at
 * any size a viewer would use it is a coloured sliver, and shown at its own
 * proportions it is a hairline. It is the right picture in exactly one place —
 * a rack, where it is what you would actually see — and the Shelf board draws it
 * there. Offering it here put a switch on the page for a thumbnail nobody could
 * read.
 *
 * A table rather than three branches: the difference between these is which
 * digest names them and what they are called, and everything else — how the URL
 * is built, how an absent one is dropped, what order they are shown in — is the
 * same for all of them and should only be written once.
 */
const ARTWORK_PANELS = [
  { id: "front", labelKey: "project.artwork.front", of: "frontSha256" },
  { id: "back", labelKey: "project.artwork.back", of: "backSha256" },
  { id: "disc", labelKey: "project.artwork.disc", of: "previewSha256" },
] as const satisfies readonly {
  id: ProjectArtworkPanelId;
  labelKey: TranslationKey;
  of: keyof LibraryProject;
}[];

/**
 * Every picture this project actually published, as something to draw.
 *
 * A digest the head does not carry is a picture that was never uploaded, and it
 * is left out rather than drawn as a box waiting for bytes that are not coming.
 * A project that predates cover artwork carries only its disc — so the list is a
 * fact about the project rather than a fixed set with gaps in it.
 *
 * Whether the bytes behind a digest can still be *fetched* is a different
 * question and not one this can answer: an object can be released, and only the
 * browser finds that out. That is handled where the picture is drawn.
 */
export const resolveProjectArtworkPanels = (project: LibraryProject): ProjectArtworkPanel[] =>
  ARTWORK_PANELS.flatMap(({ id, labelKey, of }) => {
    const contentSha256 = project[of];
    return typeof contentSha256 === "string"
      ? [{ id, labelKey, url: resolveCatalogObjectUrl({ contentSha256, kind: "preview" }) }]
      : [];
  });

/**
 * The detail-page views in their presentation order.
 *
 * Preview is a real arrangement rather than another name for Front: it places
 * the Front and Disc together, while every following view isolates one piece.
 * It therefore exists only when both of those pieces can be drawn. Missing
 * pictures leave no dead switches behind.
 */
export const resolveProjectArtworkViews = (
  panels: readonly ProjectArtworkPanel[],
): ProjectArtworkView[] => {
  const disc = panels.find((panel) => panel.id === "disc");
  const front = panels.find((panel) => panel.id === "front");
  const back = panels.find((panel) => panel.id === "back");
  const views: ProjectArtworkView[] = [];

  if (front !== undefined && disc !== undefined) {
    views.push({
      id: "preview",
      labelKey: "project.artwork.preview",
      panels: [front, disc],
    });
  }
  if (disc !== undefined) {
    views.push({ id: "disc", labelKey: disc.labelKey, panels: [disc] });
  }
  if (front !== undefined) {
    views.push({ id: "front", labelKey: front.labelKey, panels: [front] });
  }
  if (back !== undefined) {
    views.push({ id: "back", labelKey: back.labelKey, panels: [back] });
  }

  return views;
};
